import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building, Briefcase, Cpu, TrendingUp, Users, Target, Heart, DollarSign } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const AboutPage = () => {
  const navigate = useNavigate();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO 
        title="About AMP Predict | Who We Are"
        description="AMP Predict is a mock prediction market simulator built by ANJO LC. Meet the team behind the virtual event-forecasting sandbox for learners, analysts, and forecasting clubs."
        keywords="about AMP Predict, ANJO LC, prediction market education, mock trading platform, event contracts"
        url="https://amppredict.com/about"
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-[#3B82F6]/20 rounded-xl">
            <Building className="h-6 w-6 text-[#3B82F6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="about-h1">The team building the leading mock prediction market</h1>
            <p className="text-gray-400 text-sm">Powered by ANJO LC · Maryland, USA</p>
          </div>
        </div>

        {/* Main Introduction */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8 mb-6">
          <p className="text-gray-300 text-lg leading-relaxed">
            This digital asset is brought to you by <strong className="text-white">ANJO LC</strong>: A professional services firm supporting private, public, quasi-government, and all other organizations with management consulting, finance advisory, operational efficiency, technology transformation and digital assets building and implementation.
          </p>
        </div>

        {/* Events Contract Prediction */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Target className="h-6 w-6 text-[#F97316]" />
            <h2 className="text-xl font-semibold text-white">Events Contract Prediction (Informational/Educational Only)</h2>
          </div>
          <div className="space-y-4 text-gray-300">
            <p>
              ANJO LC developed AMP Predict as a digital asset/platform for users to practice event contract predictions for informational and educational purposes only—no real funds involved. Users can create contracts based on specific yes-or-no or win-lose-draw questions, set markets by buying and selling positions, and invite others to join. The platform serves as a user-driven playground for event contract prediction ideas.
            </p>
            <p>
              These Event Contracts span various topics, are user-driven and powered, and feature a mock-funded predictor where users can buy and sell yes-no or win-lose-draw event contracts without spending money.
            </p>
          </div>
        </div>

        {/* Background Regulation */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Briefcase className="h-6 w-6 text-[#3B82F6]" />
            <h2 className="text-xl font-semibold text-white">Background Regulation (2020)</h2>
          </div>
          <div className="space-y-4 text-gray-300">
            <p>
              Firms in this sector engage in trading activities under the regulated supervision of the Commodity Futures Trading Commission (CFTC) and are designated as Designated Contract Markets (DCMs), utilizing actual funds for transactions. In 2020, a firm made history by becoming the first fully regulated financial exchange in the U.S. dedicated specifically to event contracts, earning the necessary designations like established exchanges like the Chicago Mercantile Exchange (CME) and Intercontinental Exchange (ICE), thereby cementing its position. In 2024 a firm was able to offer legal election event contracts in the United States, which was another milestone.
            </p>
            <p className="text-[#F97316]">
              However, AMP Predict digital asset/platform is user led ideas coupled with admin suggestions to participate in mock event contracts prediction.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="h-6 w-6 text-green-400" />
            <h2 className="text-xl font-semibold text-white">Values</h2>
          </div>
          <p className="text-gray-300">
            Users can join a new innovative market without spending money. Both users and admins can create ideas, invite participants, set up the market, mock trade contracts, and receive mock predictions for informational and educational use. The platform offers engaging experience in this event contract market.
          </p>
        </div>

        {/* Funders */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="h-6 w-6 text-purple-400" />
            <h2 className="text-xl font-semibold text-white">Funders</h2>
          </div>
          <p className="text-gray-300">
            We are fully owned by ANJO LC with no external funding. Contact us via our website if interested. We will be making additional upgrades to the website over the course of time to improve user experience.
          </p>
        </div>

        {/* Company Info */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Company Information</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-[#1e2a45]">
              <span className="text-gray-400">Company</span>
              <span className="text-white font-medium">ANJO LC</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-[#1e2a45]">
              <span className="text-gray-400">Location</span>
              <span className="text-white">Maryland, USA</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-gray-400">Contact</span>
              <a href="mailto:ampcontactus@anjolc.com" className="text-[#3B82F6] hover:underline">
                ampcontactus@anjolc.com
              </a>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/contact')}
            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
          >
            Contact Us
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
